import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// --- Types ---

export interface LocationItem {
  id: number;
  name: string;
  slug: string;
  parent_id?: number;
  latitude?: string;
  longitude?: string;
}

export interface PropertyTypeItem {
  id: number;
  name: string;
  slug: string;
  icon?: string;
}

type PortalDomain = 'mapainmueble' | 'apartamentosguatemala' | 'casasguatemala';

interface PortalContextValue {
  domain: PortalDomain;
  domainLabel: string;
  domainPropertyTypeFilter: number | null; // null = all types
  departments: LocationItem[];
  cities: LocationItem[];
  sectors: LocationItem[];
  propertyTypes: PropertyTypeItem[];
  isLoading: boolean;
  getCitiesByDepartment: (deptId: number) => LocationItem[];
  getSectorsByCity: (cityId: number) => LocationItem[];
  resolveLocationSlug: (slug: string) => { level: 'department' | 'city' | 'sector'; id: number; name: string } | null;
}

const PortalContext = createContext<PortalContextValue | null>(null);

// --- Domain Detection ---

function detectDomain(): PortalDomain {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (host.includes('apartamentosguatemala')) return 'apartamentosguatemala';
  if (host.includes('casas-guatemala') || host.includes('casasguatemala')) return 'casasguatemala';
  return 'mapainmueble';
}

const DOMAIN_CONFIG: Record<PortalDomain, { label: string; propertyTypeFilter: number | null }> = {
  mapainmueble: { label: 'MapaInmueble', propertyTypeFilter: null },
  apartamentosguatemala: { label: 'Apartamentos Guatemala', propertyTypeFilter: 2 }, // Apartamento type ID
  casasguatemala: { label: 'Casas Guatemala', propertyTypeFilter: 1 }, // Casa type ID
};

// --- Provider ---

export function PortalProvider({ children }: { children: React.ReactNode }) {
  const [domain] = useState<PortalDomain>(detectDomain);
  const [departments, setDepartments] = useState<LocationItem[]>([]);
  const [cities, setCities] = useState<LocationItem[]>([]);
  const [sectors, setSectors] = useState<LocationItem[]>([]);
  const [propertyTypes, setPropertyTypes] = useState<PropertyTypeItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadLookupData();
  }, []);

  async function loadLookupData() {
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    try {
      const [deptRes, cityRes, sectorRes, typeRes] = await Promise.all([
        supabase.from('departments').select('id, name, slug, latitude, longitude').eq('is_active', true).order('sort_order').order('name'),
        supabase.from('cities').select('id, name, slug, department_id, latitude, longitude').eq('is_active', true).order('name'),
        supabase.from('sectors').select('id, name, slug, city_id, latitude, longitude').eq('is_active', true).order('name'),
        supabase.from('property_types').select('id, name, slug, icon').eq('is_active', true).order('sort_order'),
      ]);

      if (deptRes.data) setDepartments(deptRes.data);
      if (cityRes.data) setCities(cityRes.data.map(c => ({ ...c, parent_id: c.department_id })));
      if (sectorRes.data) setSectors(sectorRes.data.map(s => ({ ...s, parent_id: s.city_id })));
      if (typeRes.data) setPropertyTypes(typeRes.data);
    } catch (err) {
      console.error('Failed to load portal lookup data:', err);
    } finally {
      setIsLoading(false);
    }
  }

  const getCitiesByDepartment = useCallback(
    (deptId: number) => cities.filter(c => Number(c.parent_id) === Number(deptId)),
    [cities]
  );

  const getSectorsByCity = useCallback(
    (cityId: number) => sectors.filter(s => Number(s.parent_id) === Number(cityId)),
    [sectors]
  );

  const resolveLocationSlug = useCallback(
    (slug: string): { level: 'department' | 'city' | 'sector'; id: number; name: string } | null => {
      // Try cities first (sectors/zones), then departments
      const normalizedSlug = slug.toLowerCase();

      const city = cities.find(c => c.slug === normalizedSlug);
      if (city) return { level: 'city', id: city.id, name: city.name };

      const dept = departments.find(d => d.slug === normalizedSlug);
      if (dept) return { level: 'department', id: dept.id, name: dept.name };

      return null;
    },
    [departments, cities, sectors]
  );

  const config = DOMAIN_CONFIG[domain];

  return (
    <PortalContext.Provider
      value={{
        domain,
        domainLabel: config.label,
        domainPropertyTypeFilter: config.propertyTypeFilter,
        departments,
        cities,
        sectors,
        propertyTypes,
        isLoading,
        getCitiesByDepartment,
        getSectorsByCity,
        resolveLocationSlug,
      }}
    >
      {children}
    </PortalContext.Provider>
  );
}

export function usePortal() {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error('usePortal must be used within PortalProvider');
  return ctx;
}
